<!-- Automated Checks Manager component -->
<template>
  <div class="automated-checks-manager">
    <div class="header-section">
      <h2 class="title">Check & Prompt Library</h2>
      <p class="page-description">
        Manage reusable checks and their underlying AI prompts. These are the building blocks for both Document Analysis and Compliance Review Templates.
      </p>
      <div class="actions">
        <button @click="showCheckEditor" class="btn btn-primary">Create New Check</button>
        <button @click="openAIDraftModal" class="btn btn-info">Draft with AI</button>
        <button @click="openCarrierUploadModal" class="btn btn-success">
          <i class="fas fa-file-upload"></i> Upload Carrier Document
        </button>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div class="bulk-actions" v-if="filteredChecks.length > 0">
      <div class="select-all">
        <input 
          type="checkbox" 
          :checked="allSelected" 
          @change="toggleSelectAll"
          id="select-all"
        >
        <label for="select-all">Select All</label>
      </div>
      <div class="bulk-controls" v-if="selectedChecks.length > 0">
        <span class="selected-count">{{ selectedChecks.length }} selected</span>
        <button @click="confirmBulkDelete" class="btn btn-danger btn-sm">Delete Selected</button>
        <button @click="toggleSelectedEnabled(true)" class="btn btn-success btn-sm">Enable Selected</button>
        <button @click="toggleSelectedEnabled(false)" class="btn btn-warning btn-sm">Disable Selected</button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <div class="filter-group">
        <input 
          v-model="filters.search" 
          type="text" 
          class="form-control" 
          placeholder="Search by name or description..."
          @input="applyFilters"
        >
      </div>
      <div class="filter-group">
        <select v-model="filters.type" class="form-control" @change="applyFilters">
          <option value="">All Types</option>
          <option value="ANALYSIS">Analysis</option>
          <option value="COMPLIANCE">Compliance</option>
          <option value="VALIDATION">Validation</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.severity" class="form-control" @change="applyFilters">
          <option value="">All Severities</option>
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="CRITICAL">Critical</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="sortBy" class="form-control" @change="applySort">
          <option value="name">Sort by Name</option>
          <option value="created_at">Sort by Created Date</option>
          <option value="updated_at">Sort by Updated Date</option>
          <option value="severity">Sort by Severity</option>
          <option value="check_type">Sort by Type</option>
        </select>
        <button 
          @click="toggleSortDirection" 
          class="btn btn-outline-secondary"
          :title="sortDirection === 'asc' ? 'Ascending' : 'Descending'"
        >
          <i :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
        </button>
      </div>
    </div>

    <!-- List of existing checks -->
    <div class="checks-list">
      <div v-if="filteredChecks.length === 0" class="no-results">
        <p>No checks found matching your criteria</p>
      </div>
      <div v-for="check in filteredChecks" :key="check.id" class="check-item">
        <div class="check-header">
          <div class="check-header-left">
            <input 
              type="checkbox" 
              :checked="isSelected(check)" 
              @change="toggleCheckSelection(check)"
              :id="'check-' + check.id"
            >
            <h3>{{ check.name }}</h3>
            <span :class="['status-badge', check.is_enabled ? 'enabled' : 'disabled']">
              {{ check.is_enabled ? 'Enabled' : 'Disabled' }}
            </span>
          </div>
          <div class="check-actions">
            <button @click="editCheck(check)" class="btn btn-primary btn-sm">Edit</button>
            <button @click="cloneCheck(check)" class="btn btn-info btn-sm">Clone</button>
            <button @click="confirmDelete(check)" class="btn btn-danger btn-sm">Delete</button>
          </div>
        </div>
        <p class="check-description">{{ check.description }}</p>
        <div class="check-meta">
          <span class="check-type">Type: {{ check.check_type }}</span>
          <span class="check-severity">Severity: {{ check.severity }}</span>
        </div>
        
        <div class="check-details">
          <div class="actions-section">
            <h4>Actions</h4>
            <div class="actions-groups">
              <div class="action-group">
                <h5>Success Actions</h5>
                <ul v-if="check.success_actions && check.success_actions.length > 0">
                  <li v-for="action in check.success_actions" :key="action.id">
                    {{ action.name }} ({{ action.action_type }})
                  </li>
                </ul>
                <p v-else>No success actions configured</p>
              </div>
              <div class="action-group">
                <h5>Failure Actions</h5>
                <ul v-if="check.failure_actions && check.failure_actions.length > 0">
                  <li v-for="action in check.failure_actions" :key="action.id">
                    {{ action.name }} ({{ action.action_type }})
                  </li>
                </ul>
                <p v-else>No failure actions configured</p>
              </div>
            </div>
          </div>
          
          <div class="status-changes-section">
            <h4>Status Changes</h4>
            <div class="status-groups">
              <div class="status-group">
                <h5>Success Status Changes</h5>
                <ul v-if="check.success_status_changes && check.success_status_changes.length > 0">
                  <li v-for="status in check.success_status_changes" :key="status.id">
                    {{ status.name }} → {{ status.new_status }}
                  </li>
                </ul>
                <p v-else>No success status changes configured</p>
              </div>
              <div class="status-group">
                <h5>Failure Status Changes</h5>
                <ul v-if="check.failure_status_changes && check.failure_status_changes.length > 0">
                  <li v-for="status in check.failure_status_changes" :key="status.id">
                    {{ status.name }} → {{ status.new_status }}
                  </li>
                </ul>
                <p v-else>No failure status changes configured</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Check Editor Modal -->
    <Modal v-if="showCheckEditorModal" :show="true" @close="hideCheckEditor">
      <template #header>
        <h3>{{ isEditing ? 'Edit' : 'Create New' }} Automated Check</h3>
      </template>
      
      <template #body>
        <div class="modal-body">
          <div class="form-group">
            <label>Check Name</label>
            <input v-model="checkForm.name" type="text" class="form-control" required />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="checkForm.description" class="form-control" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Check Type</label>
            <select v-model="checkForm.check_type" class="form-control">
              <option value="ANALYSIS">Analysis</option>
              <option value="COMPLIANCE">Compliance</option>
              <option value="VALIDATION">Validation</option>
            </select>
          </div>
          <div class="form-group">
            <label>Severity</label>
            <select v-model="checkForm.severity" class="form-control">
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
          <div class="form-group">
            <label>Prompt Template</label>
            <p class="help-text">
              Your prompt should be clear and structured. Include specific requirements and expected response format.
            </p>
            <div class="prompt-editor">
              <textarea 
                v-model="checkForm.prompt_template" 
                class="form-control" 
                rows="5"
                @input="validatePrompt"
              ></textarea>
              <div v-if="promptValidation.issues.length > 0" class="validation-issues">
                <h6>Template Issues:</h6>
                <ul>
                  <li v-for="(issue, index) in promptValidation.issues" 
                      :key="index" 
                      :class="issue.type">
                    {{ issue.message }}
                  </li>
                </ul>
              </div>
              <div v-if="checkForm.prompt_template" class="prompt-preview">
                <h6>Preview:</h6>
                <div class="preview-content">
                  <div class="preview-header">
                    <span class="preview-label">Check Type:</span>
                    <span class="preview-value">{{ checkForm.check_type }}</span>
                    <span class="preview-label">Severity:</span>
                    <span class="preview-value" :class="'severity-' + checkForm.severity.toLowerCase()">
                      {{ checkForm.severity }}
                    </span>
                  </div>
                  <div class="preview-prompt">
                    <div v-html="formattedPrompt"></div>
                  </div>
                  <div class="preview-footer">
                    <div class="preview-section">
                      <span class="preview-label">On Success:</span>
                      <ul v-if="selectedSuccessActions.length || selectedSuccessStatusChanges.length">
                        <li v-for="action in selectedSuccessActions" :key="'sa-' + action.id">
                          Action: {{ action.name }}
                        </li>
                        <li v-for="status in selectedSuccessStatusChanges" :key="'ss-' + status.id">
                          Status Change: {{ status.name }} → {{ status.new_status }}
                        </li>
                      </ul>
                      <span v-else class="preview-empty">No success actions or status changes configured</span>
                    </div>
                    <div class="preview-section">
                      <span class="preview-label">On Failure:</span>
                      <ul v-if="selectedFailureActions.length || selectedFailureStatusChanges.length">
                        <li v-for="action in selectedFailureActions" :key="'fa-' + action.id">
                          Action: {{ action.name }}
                        </li>
                        <li v-for="status in selectedFailureStatusChanges" :key="'fs-' + status.id">
                          Status Change: {{ status.name }} → {{ status.new_status }}
                        </li>
                      </ul>
                      <span v-else class="preview-empty">No failure actions or status changes configured</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="prompt-tips">
                <h6>Template Tips:</h6>
                <ul>
                  <li>Start with a clear instruction of what to analyze</li>
                  <li>List specific items or criteria to check</li>
                  <li>Specify the expected response format</li>
                  <li>Include any special requirements or constraints</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label>Success Actions</label>
            <p class="help-text">Hold Ctrl/Cmd to select multiple actions that should be triggered when the check passes.</p>
            <select v-model="checkForm.success_actions" class="form-control select-box" multiple>
              <option v-for="action in availableActions" :key="action.id" :value="action.id">
                {{ action.name }} ({{ action.action_type }})
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Failure Actions</label>
            <p class="help-text">Hold Ctrl/Cmd to select multiple actions that should be triggered when the check fails.</p>
            <select v-model="checkForm.failure_actions" class="form-control select-box" multiple>
              <option v-for="action in availableActions" :key="action.id" :value="action.id">
                {{ action.name }} ({{ action.action_type }})
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Success Status Changes</label>
            <p class="help-text">Hold Ctrl/Cmd to select multiple status changes that should be applied when the check passes.</p>
            <select v-model="checkForm.success_status_changes" class="form-control select-box" multiple>
              <option v-for="status in availableStatusChanges" :key="status.id" :value="status.id">
                {{ status.name }} → {{ status.new_status }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Failure Status Changes</label>
            <p class="help-text">Hold Ctrl/Cmd to select multiple status changes that should be applied when the check fails.</p>
            <select v-model="checkForm.failure_status_changes" class="form-control select-box" multiple>
              <option v-for="status in availableStatusChanges" :key="status.id" :value="status.id">
                {{ status.name }} → {{ status.new_status }}
              </option>
            </select>
          </div>
        </div>
      </template>

      <template #footer>
        <button @click="saveAutomatedCheck" class="btn btn-success">
          {{ isEditing ? 'Update' : 'Save' }} Check
        </button>
        <button @click="hideCheckEditor" class="btn btn-secondary">Cancel</button>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-if="showDeleteModal" :show="true" @close="hideDeleteModal">
      <template #header>
        <h3>Confirm Delete</h3>
      </template>
      
      <template #body>
        <p>Are you sure you want to delete the automated check "{{ checkToDelete?.name }}"?</p>
        <p class="text-danger">This action cannot be undone.</p>
      </template>

      <template #footer>
        <button @click="deleteCheck" class="btn btn-danger">Delete</button>
        <button @click="hideDeleteModal" class="btn btn-secondary">Cancel</button>
      </template>
    </Modal>

    <!-- AI Draft Input Modal -->
    <Modal v-if="showAIDraftInputModal" :show="true" @close="closeAIDraftModal">
      <template #header>
        <h3>Draft Automated Check with AI</h3>
      </template>
      <template #body>
        <div class="modal-body">
          <div class="form-group">
            <label for="ai-draft-description">Describe the check you want to perform:</label>
            <textarea 
              id="ai-draft-description" 
              v-model="aiDraftText"
              class="form-control" 
              rows="8"
              placeholder="e.g., Verify that the Certificate Holder is listed exactly as Jersey Mike\'s Franchise Systems, LLC..."
            ></textarea>
          </div>
          <div v-if="aiDraftError" class="alert alert-danger">
            {{ aiDraftError }}
          </div>
        </div>
      </template>
      <template #footer>
        <button @click="submitAIDraftRequest" class="btn btn-success" :disabled="isDraftingWithAI">
          <span v-if="isDraftingWithAI" class="spinner-sm me-1"></span>
          {{ isDraftingWithAI ? 'Generating Draft...' : 'Generate Draft' }}
        </button>
        <button @click="closeAIDraftModal" class="btn btn-secondary">Cancel</button>
      </template>
    </Modal>

    <!-- Carrier Document Upload Modal -->
    <Modal v-if="showCarrierUploadModal" :show="true" @close="closeCarrierUploadModal">
      <template #header>
        <h3>
          <i class="fas fa-file-upload"></i>
          Generate Checks from Carrier Document
        </h3>
      </template>
      
      <template #body>
        <div class="modal-body carrier-upload-modal">
          <!-- Upload Form -->
          <div v-if="!extractedRequirements.length" class="upload-form">
            <div class="form-group">
              <label>Carrier Requirements Document</label>
              <div class="file-upload-area" 
                   @dragover.prevent="isDragging = true"
                   @dragleave.prevent="isDragging = false"
                   @drop.prevent="handleCarrierFileDrop"
                   :class="{ 'dragging': isDragging }">
                <input 
                  type="file" 
                  ref="carrierFileInput"
                  @change="handleCarrierFileSelect"
                  accept=".pdf,.doc,.docx"
                  style="display: none"
                />
                <div v-if="!carrierUpload.file" class="upload-placeholder" @click="$refs.carrierFileInput.click()">
                  <i class="fas fa-cloud-upload-alt fa-3x"></i>
                  <p>Click to upload or drag and drop</p>
                  <small>PDF, DOC, or DOCX files</small>
                </div>
                <div v-else class="file-selected">
                  <i class="fas fa-file-pdf fa-2x"></i>
                  <p><strong>{{ carrierUpload.file.name }}</strong></p>
                  <small>{{ formatFileSize(carrierUpload.file.size) }}</small>
                  <button @click="clearCarrierFile" class="btn btn-sm btn-danger">
                    <i class="fas fa-times"></i> Remove
                  </button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>Carrier Name</label>
              <input 
                v-model="carrierUpload.carrierName"
                type="text"
                class="form-control"
                placeholder="e.g., Verus Insurance, State Farm, etc."
                required
              />
            </div>

            <div class="form-group">
              <label>Document Type</label>
              <select v-model="carrierUpload.documentType" class="form-control">
                <option value="driver_safety_policy">Driver Safety Policy</option>
                <option value="employment_policy">Employment Policy</option>
                <option value="safety_manual">Safety Manual</option>
                <option value="procedure_manual">Procedure Manual</option>
                <option value="training_policy">Training Policy</option>
                <option value="equipment_policy">Equipment Policy</option>
                <option value="subcontractor_requirements">Subcontractor Requirements (COI)</option>
                <option value="certificate_requirements">Certificate Requirements (COI)</option>
              </select>
            </div>

            <div class="form-group">
              <label>Application Type</label>
              <select v-model="carrierUpload.applicationType" class="form-control">
                <option value="POLICY_REVIEW">Policy/Procedure Review</option>
                <option value="COI_COMPLIANCE">COI Compliance Requirements</option>
                <option value="CONTRACT_REVIEW">Contract Review</option>
              </select>
              <small class="help-text">
                <strong>Policy Review:</strong> For driver policies, safety manuals, procedures<br>
                <strong>COI Compliance:</strong> For insurance coverage requirements
              </small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="carrierUpload.usePremiumModel" />
                Use Premium Model (Gemini 1.5 Pro)
              </label>
              <small class="help-text">
                <span v-if="carrierUpload.usePremiumModel">
                  <i class="fas fa-star text-warning"></i>
                  Best for multi-page PDFs (~$0.40 per document) - Handles full documents
                </span>
                <span v-else>
                  <i class="fas fa-bolt text-info"></i>
                  Using Gemini 1.5 Flash (Fast, lower cost ~$0.10) - Good for testing
                </span>
              </small>
            </div>

            <div v-if="carrierUploadError" class="alert alert-danger">
              <i class="fas fa-exclamation-circle"></i>
              {{ carrierUploadError }}
            </div>
          </div>

          <!-- Extraction Progress -->
          <div v-if="generatingFromCarrier && !extractedRequirements.length" class="extraction-progress">
            <div class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: extractionProgress + '%' }"></div>
              </div>
              <p class="progress-text">{{ extractionStatus }}</p>
            </div>
            <div class="extraction-info">
              <i class="fas fa-robot fa-2x text-primary"></i>
              <p>AI is analyzing your document and extracting requirements...</p>
              <small>This may take 30-60 seconds depending on document size</small>
            </div>
          </div>

          <!-- Requirements Preview -->
          <div v-if="extractedRequirements.length > 0" class="requirements-preview">
            <div class="preview-header">
              <h4>
                <i class="fas fa-check-circle text-success"></i>
                Extracted {{ extractedRequirements.length }} Requirements
              </h4>
              <p class="preview-hint">Review and edit before saving</p>
            </div>

            <div class="requirements-list">
              <div 
                v-for="(req, idx) in extractedRequirements" 
                :key="idx"
                class="requirement-card"
              >
                <div class="requirement-header">
                  <div class="requirement-title">
                    <span class="requirement-code">{{ req.code }}</span>
                    <h5>{{ req.name }}</h5>
                  </div>
                  <span :class="'severity-badge severity-' + req.severity.toLowerCase()">
                    {{ req.severity }}
                  </span>
                </div>
                
                <p class="requirement-description">{{ req.description }}</p>
                
                <div class="requirement-meta">
                  <div v-if="req.applicable_document_types && req.applicable_document_types.length" class="meta-item">
                    <i class="fas fa-file"></i>
                    <span>{{ req.applicable_document_types.join(', ') }}</span>
                  </div>
                  <div v-if="req.applicable_coverage_types && req.applicable_coverage_types.length" class="meta-item">
                    <i class="fas fa-shield-alt"></i>
                    <span>{{ req.applicable_coverage_types.join(', ') }}</span>
                  </div>
                  <div v-if="req.category" class="meta-item">
                    <i class="fas fa-tag"></i>
                    <span>{{ req.category }}</span>
                  </div>
                  <div v-if="req.confidence" class="meta-item">
                    <i class="fas fa-chart-line"></i>
                    <span>Confidence: {{ (req.confidence * 100).toFixed(0) }}%</span>
                  </div>
                </div>

                <div v-if="req.source_reference" class="source-reference">
                  <small><i class="fas fa-map-marker-alt"></i> {{ req.source_reference }}</small>
                </div>
                
                <!-- Inline Edit Form -->
                <div v-if="editingExtractedIndex === idx" class="extracted-req-edit-form">
                  <div class="form-group">
                    <label>Name</label>
                    <input v-model="editingExtractedReq.name" class="form-control form-control-sm" />
                  </div>
                  <div class="form-row">
                    <div class="form-group half">
                      <label>Severity</label>
                      <select v-model="editingExtractedReq.severity" class="form-control form-control-sm">
                        <option value="CRITICAL">Critical</option>
                        <option value="HIGH">High</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="LOW">Low</option>
                      </select>
                    </div>
                    <div class="form-group half">
                      <label>Code</label>
                      <input v-model="editingExtractedReq.code" class="form-control form-control-sm" />
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Description</label>
                    <textarea v-model="editingExtractedReq.description" class="form-control form-control-sm" rows="2"></textarea>
                  </div>
                  <div class="edit-actions">
                    <button @click="saveEditedExtractedRequirement" class="btn btn-sm btn-success">
                      <i class="fas fa-check"></i> Save
                    </button>
                    <button @click="cancelEditExtractedRequirement" class="btn btn-sm btn-secondary">
                      Cancel
                    </button>
                  </div>
                </div>

                <div class="requirement-actions">
                  <button @click="editExtractedRequirement(idx)" class="btn btn-sm btn-outline-primary">
                    <i class="fas fa-edit"></i> Edit
                  </button>
                  <button @click="removeExtractedRequirement(idx)" class="btn btn-sm btn-outline-danger">
                    <i class="fas fa-trash"></i> Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <template #footer>
        <div v-if="!extractedRequirements.length">
          <button 
            @click="generateChecksFromCarrierDoc"
            :disabled="!carrierUpload.file || !carrierUpload.carrierName || generatingFromCarrier"
            class="btn btn-success"
          >
            <i class="fas fa-magic"></i>
            {{ generatingFromCarrier ? 'Extracting...' : 'Extract Requirements' }}
          </button>
          <button @click="closeCarrierUploadModal" class="btn btn-secondary">Cancel</button>
        </div>
        <div v-else>
          <button @click="saveExtractedRequirements" class="btn btn-primary">
            <i class="fas fa-save"></i>
            Save All {{ extractedRequirements.length }} Requirements
          </button>
          <button @click="cancelExtraction" class="btn btn-secondary">Cancel</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import api from '../services/api';
import Modal from './Modal.vue';

export default {
  name: 'AutomatedChecksManager',
  components: { Modal },
  setup() {
    const automatedChecks = ref([]);
    const showCheckEditorModal = ref(false);
    const showDeleteModal = ref(false);
    const checkToDelete = ref(null);
    const isEditing = ref(false);
    const availableActions = ref([]);
    const availableStatusChanges = ref([]);
    
    // State for AI Draft Modal
    const showAIDraftInputModal = ref(false);
    const aiDraftText = ref('');
    const isDraftingWithAI = ref(false);
    const aiDraftError = ref(null);
    
    // State for Carrier Document Upload Modal
    const showCarrierUploadModal = ref(false);
    const carrierUpload = ref({
      file: null,
      carrierName: '',
      documentType: 'driver_safety_policy',
      applicationType: 'POLICY_REVIEW',
      usePremiumModel: true
    });
    const generatingFromCarrier = ref(false);
    const extractionProgress = ref(0);
    const extractionStatus = ref('');
    const extractedRequirements = ref([]);
    const carrierUploadError = ref(null);
    const currentExtractionId = ref(null);
    const isDragging = ref(false);
    
    const checkForm = ref({
      id: null,
      name: '',
      description: '',
      check_type: 'ANALYSIS',
      severity: 'MEDIUM',
      prompt_template: '',
      success_actions: [],
      failure_actions: [],
      success_status_changes: [],
      failure_status_changes: []
    });

    const filters = ref({
      search: '',
      type: '',
      severity: ''
    });
    const sortBy = ref('name');
    const sortDirection = ref('asc');

    const promptValidation = ref({
      issues: []
    });

    const formattedPrompt = computed(() => {
      const template = checkForm.value.prompt_template;
      // Ensure template is a string before trying to split it
      if (typeof template !== 'string') {
        return ''; // Return empty string if not a string (or handle as error)
      }
      if (!template) return ''; // Already handles empty string or null/undefined after coercion
      
      // Convert the template to HTML with proper formatting
      return template
        .split('\n')
        .map(line => {
          // Format numbered lists
          line = line.replace(/(\d+\.)\s+(.+)/, '<span class="list-number">$1</span> $2');
          // Format bullet points
          line = line.replace(/[-*]\s+(.+)/, '<span class="list-bullet">•</span> $1');
          // Highlight key phrases
          line = line.replace(/(analyze|check|review|response format)/gi, 
            '<span class="highlight">$1</span>');
          return line;
        })
        .join('<br>');
    });

    const selectedSuccessActions = computed(() => {
      return availableActions.value.filter(action => 
        checkForm.value.success_actions.includes(action.id)
      );
    });

    const selectedFailureActions = computed(() => {
      return availableActions.value.filter(action => 
        checkForm.value.failure_actions.includes(action.id)
      );
    });

    const selectedSuccessStatusChanges = computed(() => {
      return availableStatusChanges.value.filter(status => 
        checkForm.value.success_status_changes.includes(status.id)
      );
    });

    const selectedFailureStatusChanges = computed(() => {
      return availableStatusChanges.value.filter(status => 
        checkForm.value.failure_status_changes.includes(status.id)
      );
    });

    const filteredChecks = computed(() => {
      let result = [...automatedChecks.value];
      
      // Apply filters
      if (filters.value.search) {
        const searchTerm = filters.value.search.toLowerCase();
        result = result.filter(check => 
          check.name.toLowerCase().includes(searchTerm) ||
          check.description.toLowerCase().includes(searchTerm)
        );
      }
      
      if (filters.value.type) {
        result = result.filter(check => check.check_type === filters.value.type);
      }
      
      if (filters.value.severity) {
        result = result.filter(check => check.severity === filters.value.severity);
      }
      
      // Apply sorting
      const severityOrder = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
      result.sort((a, b) => {
        let compareA = a[sortBy.value];
        let compareB = b[sortBy.value];
        
        // Special handling for severity sorting
        if (sortBy.value === 'severity') {
          compareA = severityOrder[a.severity];
          compareB = severityOrder[b.severity];
        }
        
        if (compareA < compareB) return sortDirection.value === 'asc' ? -1 : 1;
        if (compareA > compareB) return sortDirection.value === 'asc' ? 1 : -1;
        return 0;
      });
      
      return result;
    });

    const applyFilters = () => {
      // This is just to trigger the computed property
      // The actual filtering is done in the computed property
    };

    const applySort = () => {
      // This is just to trigger the computed property
      // The actual sorting is done in the computed property
    };

    const toggleSortDirection = () => {
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    };

    const loadAvailableActions = async () => {
      try {
        const response = await api.get('/actions/');
        availableActions.value = response.data || [];
      } catch (error) {
        console.error('Error loading actions:', error);
      }
    };

    const loadAvailableStatusChanges = async () => {
      try {
        const response = await api.get('/status-changes/');
        availableStatusChanges.value = response.data || [];
      } catch (error) {
        console.error('Error loading status changes:', error);
      }
    };

    const loadAutomatedChecks = async () => {
      try {
        const response = await api.get('/automated-checks/');
        if (response?.data?.automated_checks) {
          automatedChecks.value = response.data.automated_checks;
        } else {
          throw new Error('Invalid response format from server');
        }
      } catch (error) {
        console.error('Error loading automated checks:', error);
      }
    };

    const showCheckEditor = () => {
      checkForm.value = {
        id: null,
        name: '',
        description: '',
        check_type: 'ANALYSIS',
        severity: 'MEDIUM',
        prompt_template: '',
        success_actions: [],
        failure_actions: [],
        success_status_changes: [],
        failure_status_changes: []
      };
      isEditing.value = false;
      showCheckEditorModal.value = true;
    };

    const editCheck = (check) => {
      checkForm.value = {
        id: check.id,
        name: check.name,
        description: check.description,
        check_type: check.check_type,
        severity: check.severity,
        prompt_template: check.prompt_template,
        success_actions: check.success_actions?.map(a => a.id) || [],
        failure_actions: check.failure_actions?.map(a => a.id) || [],
        success_status_changes: check.success_status_changes?.map(s => s.id) || [],
        failure_status_changes: check.failure_status_changes?.map(s => s.id) || []
      };
      isEditing.value = true;
      showCheckEditorModal.value = true;
    };

    const hideCheckEditor = () => {
      showCheckEditorModal.value = false;
      isEditing.value = false;
    };

    const confirmDelete = (check) => {
      checkToDelete.value = check;
      showDeleteModal.value = true;
    };

    const hideDeleteModal = () => {
      showDeleteModal.value = false;
      checkToDelete.value = null;
    };

    const deleteCheck = async () => {
      try {
        if (!checkToDelete.value?.id) {
          alert('No check selected for deletion');
          return;
        }

        await api.delete(`/automated-checks/${checkToDelete.value.id}/`);
        await loadAutomatedChecks();
        hideDeleteModal();
        alert('Automated check deleted successfully');
      } catch (error) {
        console.error('Error deleting check:', error);
        alert('Error deleting check: ' + error.message);
      }
    };

    const saveAutomatedCheck = async () => {
      try {
        if (!checkForm.value.name) {
          alert('Check name is required');
          return;
        }

        if (!checkForm.value.prompt_template) {
          alert('Prompt template is required');
          return;
        }

        const checkPayload = {
          name: checkForm.value.name,
          description: checkForm.value.description || '',
          check_type: checkForm.value.check_type,
          severity: checkForm.value.severity,
          is_system: false,
          is_visible_to_users: true,
          prompt_template: checkForm.value.prompt_template,
          requirements: { response_format: "json" },
          success_action_ids: checkForm.value.success_actions || [],
          failure_action_ids: checkForm.value.failure_actions || [],
          success_status_change_ids: checkForm.value.success_status_changes || [],
          failure_status_change_ids: checkForm.value.failure_status_changes || []
        };

        if (isEditing.value) {
          await api.put(`/automated-checks/${checkForm.value.id}/`, checkPayload);
        } else {
          await api.post('/automated-checks/', checkPayload);
        }

        await loadAutomatedChecks();
        hideCheckEditor();
        alert(`Automated check ${isEditing.value ? 'updated' : 'created'} successfully`);
      } catch (error) {
        console.error('Error saving automated check:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
        alert(`Error ${isEditing.value ? 'updating' : 'saving'} automated check: ` + errorMessage);
      }
    };

    const validatePrompt = () => {
      const issues = [];
      const template = checkForm.value.prompt_template;

      // Check minimum length
      if (template.length < 50) {
        issues.push({
          type: 'warning',
          message: 'Template seems too short. Consider adding more detail.'
        });
      }

      // Check if it includes instructions
      if (!template.toLowerCase().includes('analyze') && 
          !template.toLowerCase().includes('check') && 
          !template.toLowerCase().includes('review')) {
        issues.push({
          type: 'error',
          message: 'Template should include clear instruction words (e.g., "analyze", "check", "review")'
        });
      }

      // Check for numbered or bulleted list
      if (!template.match(/(\d+\.|[-*])\s+\w+/)) {
        issues.push({
          type: 'warning',
          message: 'Consider using a numbered or bulleted list to structure requirements'
        });
      }

      // Check for response format specification
      if (!template.toLowerCase().includes('response') || 
          !template.toLowerCase().includes('format')) {
        issues.push({
          type: 'error',
          message: 'Template should specify the expected response format'
        });
      }

      // Check for common placeholders that should be replaced
      if (template.includes('[') || template.includes(']')) {
        issues.push({
          type: 'warning',
          message: 'Replace all [placeholder] text with actual requirements'
        });
      }

      promptValidation.value.issues = issues;
    };

    const cloneCheck = (check) => {
      checkForm.value = {
        id: null, // New check, so no ID
        name: `${check.name} (Copy)`,
        description: check.description,
        check_type: check.check_type,
        severity: check.severity,
        prompt_template: check.prompt_template,
        success_actions: check.success_actions?.map(a => a.id) || [],
        failure_actions: check.failure_actions?.map(a => a.id) || [],
        success_status_changes: check.success_status_changes?.map(s => s.id) || [],
        failure_status_changes: check.failure_status_changes?.map(s => s.id) || []
      };
      isEditing.value = false; // Treat as new check
      showCheckEditorModal.value = true;
    };

    const selectedChecks = ref([]);
    
    const allSelected = computed(() => {
      return filteredChecks.value.length > 0 && 
             filteredChecks.value.every(check => isSelected(check));
    });

    const isSelected = (check) => {
      return selectedChecks.value.includes(check.id);
    };

    const toggleSelectAll = () => {
      if (allSelected.value) {
        selectedChecks.value = [];
      } else {
        selectedChecks.value = filteredChecks.value.map(check => check.id);
      }
    };

    const toggleCheckSelection = (check) => {
      const index = selectedChecks.value.indexOf(check.id);
      if (index === -1) {
        selectedChecks.value.push(check.id);
      } else {
        selectedChecks.value.splice(index, 1);
      }
    };

    const confirmBulkDelete = () => {
      if (selectedChecks.value.length === 0) return;
      
      if (confirm(`Are you sure you want to delete ${selectedChecks.value.length} checks? This action cannot be undone.`)) {
        bulkDelete();
      }
    };

    const bulkDelete = async () => {
      try {
        await Promise.all(
          selectedChecks.value.map(id => 
            api.delete(`/automated-checks/${id}/`)
          )
        );
        await loadAutomatedChecks();
        selectedChecks.value = [];
        alert('Selected checks deleted successfully');
      } catch (error) {
        console.error('Error deleting checks:', error);
        alert('Error deleting checks: ' + error.message);
      }
    };

    const toggleSelectedEnabled = async (enabled) => {
      if (selectedChecks.value.length === 0) return;
      
      try {
        await Promise.all(
          selectedChecks.value.map(id => 
            api.patch(`/automated-checks/${id}/`, { is_enabled: enabled })
          )
        );
        await loadAutomatedChecks();
        alert(`Selected checks ${enabled ? 'enabled' : 'disabled'} successfully`);
      } catch (error) {
        console.error('Error updating checks:', error);
        alert('Error updating checks: ' + error.message);
      }
    };

    const openAIDraftModal = () => {
      aiDraftText.value = '';
      aiDraftError.value = null;
      showAIDraftInputModal.value = true;
    };

    const closeAIDraftModal = () => {
      showAIDraftInputModal.value = false;
    };

    const submitAIDraftRequest = async () => {
      if (!aiDraftText.value.trim()) {
        aiDraftError.value = 'Please enter a description for the check.';
        return;
      }
      isDraftingWithAI.value = true;
      aiDraftError.value = null;

      try {
        const response = await api.post('/assist/generate-check-requirement-draft/', {
          text_description: aiDraftText.value
        });
        
        const draft = response.data;

        // Populate the main checkForm with the draft data
        checkForm.value = {
          id: null, // New draft, so no ID
          name: draft.name || '',
          description: draft.description || aiDraftText.value, // Fallback to user text if no desc from AI
          check_type: checkForm.value.check_type, // Keep current check_type or default, AI might suggest one later
          severity: draft.suggested_severity || 'MEDIUM',
          prompt_template: draft.validation_logic?.prompt_template || '',
          // validation_logic.type and parameters are part of the prompt_template for now
          // Or, we could store them if checkForm is adapted:
          // validation_logic_type: draft.validation_logic?.type || '',
          // validation_logic_parameters: draft.validation_logic?.parameters || {},
          success_actions: [], // Start with no actions/status changes for a new draft
          failure_actions: [],
          success_status_changes: [],
          failure_status_changes: []
        };

        isEditing.value = false; 
        closeAIDraftModal();
        showCheckEditorModal.value = true; // Open the main editor, pre-filled

      } catch (error) {
        console.error('Error generating AI draft:', error);
        if (error.response && error.response.data && error.response.data.error) {
          aiDraftError.value = error.response.data.error;
        } else {
          aiDraftError.value = 'Failed to generate draft. Please try again.';
        }
      } finally {
        isDraftingWithAI.value = false;
      }
    };

    // ===== CARRIER DOCUMENT UPLOAD METHODS =====
    
    const openCarrierUploadModal = () => {
      showCarrierUploadModal.value = true;
      carrierUploadError.value = null;
      extractedRequirements.value = [];
      generatingFromCarrier.value = false;
      extractionProgress.value = 0;
    };
    
    const closeCarrierUploadModal = () => {
      showCarrierUploadModal.value = false;
      carrierUpload.value = {
        file: null,
        carrierName: '',
        documentType: 'driver_safety_policy',
        applicationType: 'POLICY_REVIEW',
        usePremiumModel: true
      };
      extractedRequirements.value = [];
      currentExtractionId.value = null;
    };
    
    const handleCarrierFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
        carrierUpload.value.file = file;
        carrierUploadError.value = null;
      }
    };
    
    const handleCarrierFileDrop = (event) => {
      isDragging.value = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        carrierUpload.value.file = file;
        carrierUploadError.value = null;
      }
    };
    
    const clearCarrierFile = () => {
      carrierUpload.value.file = null;
    };
    
    const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };
    
    const generateChecksFromCarrierDoc = async () => {
      if (!carrierUpload.value.file || !carrierUpload.value.carrierName) {
        carrierUploadError.value = 'Please provide both a file and carrier name';
        return;
      }
      
      generatingFromCarrier.value = true;
      extractionProgress.value = 0;
      extractionStatus.value = 'Uploading document...';
      carrierUploadError.value = null;
      
      try {
        const formData = new FormData();
        formData.append('file', carrierUpload.value.file);
        formData.append('carrier_name', carrierUpload.value.carrierName);
        formData.append('document_type', carrierUpload.value.documentType);
        formData.append('application_type', carrierUpload.value.applicationType);
        formData.append('use_premium_model', carrierUpload.value.usePremiumModel);
        
        extractionProgress.value = 10;
        extractionStatus.value = 'Analyzing document with AI...';
        
        const response = await api.post(
          '/carrier-extraction/generate/',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              extractionProgress.value = Math.min(progress, 90);
            }
          }
        );
        
        extractionProgress.value = 100;
        extractionStatus.value = 'Extraction complete!';
        
        // Store extraction ID and requirements
        currentExtractionId.value = response.data.extraction_id;
        extractedRequirements.value = response.data.requirements;
        
        
      } catch (error) {
        console.error('Error generating checks from carrier document:', error);
        carrierUploadError.value = error.response?.data?.error || error.message || 'Failed to extract requirements';
        extractionProgress.value = 0;
        extractionStatus.value = '';
      } finally {
        generatingFromCarrier.value = false;
      }
    };
    
    const editingExtractedIndex = ref(null);
    const editingExtractedReq = ref(null);

    const editExtractedRequirement = (index) => {
      editingExtractedIndex.value = index;
      editingExtractedReq.value = { ...extractedRequirements.value[index] };
    };

    const saveEditedExtractedRequirement = () => {
      if (editingExtractedIndex.value !== null && editingExtractedReq.value) {
        extractedRequirements.value[editingExtractedIndex.value] = { ...editingExtractedReq.value };
      }
      editingExtractedIndex.value = null;
      editingExtractedReq.value = null;
    };

    const cancelEditExtractedRequirement = () => {
      editingExtractedIndex.value = null;
      editingExtractedReq.value = null;
    };
    
    const removeExtractedRequirement = (index) => {
      if (confirm('Remove this requirement from the extraction?')) {
        extractedRequirements.value.splice(index, 1);
      }
    };
    
    const saveExtractedRequirements = async () => {
      if (!currentExtractionId.value) {
        carrierUploadError.value = 'No extraction ID found';
        return;
      }
      
      try {
        const response = await api.post(
          `/carrier-extraction/${currentExtractionId.value}/publish/`,
          {
            check_definition_name: `${carrierUpload.value.carrierName} - ${carrierUpload.value.documentType}`,
            check_definition_description: `Requirements extracted from ${carrierUpload.value.carrierName} ${carrierUpload.value.documentType}`,
            project_type: 'INSURANCE_REVIEW'
          }
        );
        
        alert(`Successfully created check definition with ${response.data.requirements_published} requirements!`);
        
        // Close modal and refresh checks
        closeCarrierUploadModal();
        await loadAutomatedChecks();
        
      } catch (error) {
        console.error('Error saving requirements:', error);
        carrierUploadError.value = error.response?.data?.error || 'Failed to save requirements';
      }
    };
    
    const cancelExtraction = () => {
      if (confirm('Discard extracted requirements?')) {
        extractedRequirements.value = [];
        currentExtractionId.value = null;
      }
    };

    onMounted(async () => {
      try {
        await Promise.all([
          loadAutomatedChecks(),
          loadAvailableActions(),
          loadAvailableStatusChanges()
        ]);
      } catch (error) {
        console.error('Error during data loading:', error);
      }
    });

    return {
      automatedChecks,
      showCheckEditorModal,
      showDeleteModal,
      checkToDelete,
      isEditing,
      availableActions,
      availableStatusChanges,
      checkForm,
      showCheckEditor,
      hideCheckEditor,
      editCheck,
      cloneCheck,
      confirmDelete,
      hideDeleteModal,
      deleteCheck,
      saveAutomatedCheck,
      filters,
      sortBy,
      sortDirection,
      filteredChecks,
      applyFilters,
      applySort,
      toggleSortDirection,
      promptValidation,
      validatePrompt,
      formattedPrompt,
      selectedSuccessActions,
      selectedFailureActions,
      selectedSuccessStatusChanges,
      selectedFailureStatusChanges,
      selectedChecks,
      allSelected,
      isSelected,
      toggleSelectAll,
      toggleCheckSelection,
      confirmBulkDelete,
      toggleSelectedEnabled,
      showAIDraftInputModal,
      aiDraftText,
      isDraftingWithAI,
      aiDraftError,
      openAIDraftModal,
      closeAIDraftModal,
      submitAIDraftRequest,
      // Carrier upload
      showCarrierUploadModal,
      carrierUpload,
      generatingFromCarrier,
      extractionProgress,
      extractionStatus,
      extractedRequirements,
      carrierUploadError,
      isDragging,
      openCarrierUploadModal,
      closeCarrierUploadModal,
      handleCarrierFileSelect,
      handleCarrierFileDrop,
      clearCarrierFile,
      formatFileSize,
      generateChecksFromCarrierDoc,
      editExtractedRequirement,
      editingExtractedIndex,
      editingExtractedReq,
      saveEditedExtractedRequirement,
      cancelEditExtractedRequirement,
      removeExtractedRequirement,
      saveExtractedRequirements,
      cancelExtraction
    };
  }
};
</script>

<style scoped>
.automated-checks-manager {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.title {
  margin: 0;
}

.page-description {
  margin-bottom: 1.5rem;
  color: #6c757d;
}

.checks-list {
  display: grid;
  gap: 20px;
}

.check-item {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background-color: white;
}

.check-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.check-description {
  color: #666;
  margin-bottom: 15px;
}

.check-meta {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.check-type, .check-severity {
  padding: 4px 12px;
  border-radius: 12px;
  background-color: #f0f0f0;
  font-size: 0.9em;
}

.check-details {
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.actions-section,
.status-changes-section {
  margin-bottom: 20px;
}

.actions-groups,
.status-groups {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 10px;
}

.action-group,
.status-group {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
}

.action-group h5,
.status-group h5 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #495057;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}

li:last-child {
  border-bottom: none;
}

select[multiple] {
  height: 100px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-control {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 0.9em;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
}

.check-actions {
  display: flex;
  gap: 8px;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.text-danger {
  color: #dc3545;
}

.help-text {
  font-size: 0.9em;
  color: #666;
  margin: 5px 0;
}

.select-box {
  height: 150px !important;
  padding: 8px;
}

.select-box option {
  padding: 8px;
  margin: 2px 0;
  border-radius: 4px;
}

.select-box option:hover {
  background-color: #e9ecef;
}

.select-box option:checked {
  background-color: #007bff;
  color: white;
}

.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  flex: 1;
  min-width: 200px;
  display: flex;
  gap: 8px;
}

.filter-group .form-control {
  flex: 1;
}

.filter-group .btn-outline-secondary {
  padding: 8px 12px;
  border: 1px solid #ddd;
}

.no-results {
  text-align: center;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #6c757d;
}

.no-results p {
  margin: 0;
  font-size: 1.1em;
}

.prompt-editor {
  position: relative;
  margin-bottom: 15px;
}

.validation-issues {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  background-color: #fff3cd;
}

.validation-issues h6 {
  color: #856404;
  margin-bottom: 8px;
}

.validation-issues ul {
  margin: 0;
  padding-left: 20px;
}

.validation-issues li {
  margin-bottom: 5px;
}

.validation-issues li.error {
  color: #721c24;
}

.validation-issues li.warning {
  color: #856404;
}

.prompt-tips {
  margin-top: 15px;
  padding: 10px;
  border: 1px solid #d6d8db;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.prompt-tips h6 {
  color: #495057;
  margin-bottom: 8px;
}

.prompt-tips ul {
  margin: 0;
  padding-left: 20px;
}

.prompt-tips li {
  color: #6c757d;
  margin-bottom: 5px;
}

.prompt-preview {
  margin: 15px 0;
  border: 1px solid #d1ecf1;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.prompt-preview h6 {
  margin: 0;
  padding: 10px;
  background-color: #d1ecf1;
  color: #0c5460;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.preview-content {
  padding: 15px;
}

.preview-header {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
}

.preview-label {
  font-weight: bold;
  color: #495057;
}

.preview-value {
  color: #6c757d;
}

.preview-value.severity-critical { color: #dc3545; }
.preview-value.severity-high { color: #fd7e14; }
.preview-value.severity-medium { color: #ffc107; }
.preview-value.severity-low { color: #28a745; }

.preview-prompt {
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  white-space: pre-wrap;
}

.preview-prompt .list-number {
  color: #007bff;
  font-weight: bold;
}

.preview-prompt .list-bullet {
  color: #007bff;
  font-weight: bold;
  margin-right: 5px;
}

.preview-prompt .highlight {
  background-color: #fff3cd;
  padding: 0 2px;
  border-radius: 2px;
}

.preview-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.preview-section {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
}

.preview-section ul {
  margin: 10px 0 0 0;
  padding-left: 20px;
}

.preview-section li {
  color: #6c757d;
  margin-bottom: 5px;
}

.preview-empty {
  color: #6c757d;
  font-style: italic;
  font-size: 0.9em;
}

.actions {
  display: flex;
  gap: 10px;
}

.bulk-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.select-all {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-all input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.bulk-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selected-count {
  font-weight: bold;
  color: #495057;
  margin-right: 10px;
}

.check-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.check-header-left input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.check-header-left h3 {
  margin: 0;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 500;
}

.status-badge.enabled {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.disabled {
  background-color: #f8d7da;
  color: #721c24;
}

/* ===== CARRIER DOCUMENT UPLOAD STYLES ===== */

.carrier-upload-modal {
  min-height: 400px;
}

.file-upload-area {
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  background: #f7fafc;
}

.file-upload-area:hover {
  border-color: #4299e1;
  background: #ebf8ff;
}

.file-upload-area.dragging {
  border-color: #3182ce;
  background: #bee3f8;
}

.upload-placeholder {
  cursor: pointer;
}

.upload-placeholder i {
  color: #4299e1;
  margin-bottom: 1rem;
}

.upload-placeholder p {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #2d3748;
}

.upload-placeholder small {
  color: #718096;
}

.file-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.file-selected i {
  color: #e53e3e;
}

.file-selected p {
  margin: 0;
  color: #2d3748;
}

.file-selected small {
  color: #718096;
}

.extraction-progress {
  padding: 2rem;
  text-align: center;
}

.progress-container {
  margin-bottom: 2rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #805ad5);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
}

.extraction-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.extraction-info i {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.extraction-info p {
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
  color: #2d3748;
}

.extraction-info small {
  color: #718096;
}

.requirements-preview {
  max-height: 600px;
  overflow-y: auto;
}

.preview-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.preview-header h4 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
}

.preview-hint {
  margin: 0;
  color: #718096;
  font-size: 0.9rem;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.requirement-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.requirement-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.requirement-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.requirement-title {
  flex: 1;
}

.requirement-code {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.75rem;
  color: #718096;
  background: #edf2f7;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 0.5rem;
}

.requirement-card h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.severity-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.severity-critical {
  background: #fee2e2;
  color: #991b1b;
}

.severity-high {
  background: #fed7aa;
  color: #9a3412;
}

.severity-medium {
  background: #fef3c7;
  color: #92400e;
}

.severity-low {
  background: #dbeafe;
  color: #1e40af;
}

.requirement-description {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0.75rem 0;
}

.requirement-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0.75rem 0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #718096;
}

.meta-item i {
  color: #a0aec0;
}

.source-reference {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.source-reference small {
  color: #a0aec0;
  font-size: 0.8rem;
}

.requirement-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  cursor: pointer;
}

.help-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #718096;
  line-height: 1.4;
}

.text-warning {
  color: #d69e2e;
}

.text-info {
  color: #4299e1;
}

.text-success {
  color: #38a169;
}

.text-primary {
  color: #4299e1;
}

.btn-outline-primary {
  border: 1px solid #4299e1;
  color: #4299e1;
  background: white;
}

.btn-outline-primary:hover {
  background: #4299e1;
  color: white;
}

.btn-outline-danger {
  border: 1px solid #e53e3e;
  color: #e53e3e;
  background: white;
}

.btn-outline-danger:hover {
  background: #e53e3e;
  color: white;
}
</style> 